import { useAccessibleBuilders } from '@ctw/shared/api/auth-service/accessible-builders';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { PatientHistoryRequestModel } from '@ctw/shared/api/fhir/models/patient-history';
import { useNonThirdPartyPatientsByUPID } from '@ctw/shared/api/fhir/patient-helper';
import {
  type PatientHistoryJobResponse,
  generateDemoPatientHistoryJob,
} from '@ctw/shared/api/patient-history/patient-history-types';
import type { GetPatientHistoryJobsParams } from '@ctw/shared/content/patient-history/use-patient-history';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getZusApiBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_PATIENT_HISTORY_DETAILS } from '@ctw/shared/utils/query-keys';
import { format } from 'date-fns';
import { omitBy } from 'lodash-es';
import { useMemo } from 'react';

export type PatientHistoryResponseError = {
  // TODO: Can code be a list of status codes? Do we have that type defined anywhere.
  code: number;
  title: string;
  details: string;
};

export interface PatientHistoryErrorResponse {
  id: string;
  detail: string;
}

export const schedulePatientHistory = async (
  ctw: CTWState,
  resultData: { id: string; npi: string; role: string; name: string },
) => {
  const endpointUrl = `${getZusApiBaseUrl(
    ctw.requestContext.env,
    // If patientID is empty, just pass any non-identifying string in url.
  )}/patient-history/jobs`;

  const body = {
    data: {
      type: 'patient-history/jobs',
      attributes: {
        requestConsent: true,
        priority: true,
        practitioner: {
          npi: resultData.npi,
          name: resultData.name,
          role: resultData.role.toLocaleLowerCase(),
        },
      },
      relationships: {
        patient: {
          data: { type: 'fhir/Patient', id: resultData.id },
        },
      },
    },
  };

  const response = await ctw.ctwFetch(endpointUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ctw.requestContext.authToken}`,
      'Zus-Account': ctw.requestContext.builderId,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorMessage =
      (response.data as PatientHistoryErrorResponse).detail ?? 'Failed to schedule patient history';
    throw new Error(errorMessage);
  }

  return response.data;
};

export async function getPatientHistoryJobs({
  ctwFetch,
  requestContext,
  count = 50,
  offset = 0,
  patientId,
  builderId,
  status,
  excludeFutureJobs,
}: GetPatientHistoryJobsParams): Promise<{ ok: boolean; data: PatientHistoryJobResponse }> {
  const baseUrl = new URL(`${getZusApiBaseUrl(requestContext.env)}/patient-history/jobs?`);

  const paramsObj = omitBy(
    {
      'page[count]': String(count),
      'page[offset]': String(offset * count),
      'filter[builder-id]': builderId,
      'filter[patient-id]': patientId ? `${patientId}` : '',
      'filter[status]': status ? `${status}` : '',
      ...(!!excludeFutureJobs && {
        'filter[targetDate][until]': format(Date.now(), 'yyyy-MM-dd'),
      }),
    },
    (value) => !value,
  );

  const params = new URLSearchParams([...Object.entries(paramsObj)]).toString();
  const endpointUrl = new URL(`${baseUrl}${decodeURIComponent(params)}`);

  const response = await ctwFetch<PatientHistoryJobResponse>(endpointUrl.href, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Zus-Account': builderId,
    },
  });

  return { ok: response.ok, data: response.data };
}

// Callers pre-filter `patients` to those owned by builders the user has access
// to (via `useAccessibleBuilders`). The DA endpoint still returns 403 in edge
// cases — different access systems can drift — so any non-OK response per
// patient is logged and skipped rather than thrown, so one bad builder never
// blocks the rest.
export const getPatientHistoryDetails = async (
  ctwFetch: CTWState['ctwFetch'],
  requestContext: CTWRequestContext,
  patients: Array<PatientModel>,
  telemetry: ReturnType<typeof useTelemetry>,
): Promise<PatientHistoryDetails> => {
  const responses = await Promise.all(
    patients.map(async (patient) => {
      const { ok, data: response } = await getPatientHistoryJobs({
        ctwFetch,
        requestContext,
        patientId: patient.id,
        builderId: patient.builderId,
      });
      if (!ok) {
        telemetry.logger.warn('Skipping patient history details for inaccessible builder', {
          patientId: patient.id,
          builderId: patient.builderId,
        });
        return [];
      }
      return response.data.map((job) => new PatientHistoryRequestModel(patient, job));
    }),
  );

  const now = new Date();

  // Sort all jobs in descending order by target date
  // Hypothetically, the order should be scheduled -> in-progress -> completed
  const jobs = responses
    .flat()
    .sort((a, b) => b.rawTargetDate.getTime() - a.rawTargetDate.getTime());

  // Get the  most recent scheduled job, which is the last scheduled job from the list ignoring all jobs in the past.
  const nextScheduled = jobs.findLast((job) => job.isScheduled && job.rawTargetDate > now);
  // Get the most recent completed job, which is the the first completed job from the list
  const completed = jobs.find((job) => job.isCompleted);
  // Get the current in-progress job, which is the first in-progress job from the list and the target date must be after the completed job
  const inProgress = jobs.find(
    (job) => job.isInProgress && (completed ? job.rawTargetDate > completed.rawTargetDate : true),
  );

  return {
    nextScheduledAt: nextScheduled?.rawTargetDate,
    completed,
    inProgress,
  };
};

export interface PatientHistoryDetails {
  // The most recent completed job
  completed?: PatientHistoryRequestModel;
  // The most recent in-progress job. If there's a more recent completed job, then we treat the in-progress job as
  // stale and do not return it here
  inProgress?: PatientHistoryRequestModel;
  // The next scheduled job that's in the future
  nextScheduledAt?: Date;
}

export function usePatientHistoryDetails() {
  const crossBuilderLookup = useFeatureFlag('ctw-cross-builder-upid-lookup');
  const patients = useNonThirdPartyPatientsByUPID(crossBuilderLookup);
  const accessibleBuilders = useAccessibleBuilders();

  // Put the patients data into a list of fhir id's to pass to the query
  const patientFhirIds = patients.data?.map((patient) => patient.id);
  const accessibleBuilderIds = useMemo(
    () => accessibleBuilders.data?.map((builder) => builder.id),
    [accessibleBuilders.data],
  );

  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_HISTORY_DETAILS,
    queryKey: [patientFhirIds, accessibleBuilderIds],
    queryFn: async ({ ctwFetch, requestContext, patient, telemetry }) => {
      const accessibleBuilderIdSet = new Set(accessibleBuilderIds);
      const accessiblePatients = (patients.data ?? [patient]).filter((p) =>
        accessibleBuilderIdSet.has(p.builderId),
      );

      const patientHistoryDetails = await getPatientHistoryDetails(
        ctwFetch,
        requestContext,
        accessiblePatients,
        telemetry,
      );

      // If a test/demo patient doesn't have a package then use this demo content.
      if (
        patient.isTestPatient &&
        !patientHistoryDetails.completed &&
        !patientHistoryDetails.inProgress &&
        !patientHistoryDetails.nextScheduledAt
      ) {
        return {
          inProgress: new PatientHistoryRequestModel(
            patient,
            generateDemoPatientHistoryJob(patient.id),
          ),
          completed: new PatientHistoryRequestModel(
            patient,
            generateDemoPatientHistoryJob(patient.id),
          ),
          nextScheduledAt: new Date(),
        };
      }
      return patientHistoryDetails;
    },
    enabled: Boolean(patients.data && accessibleBuilders.data),
  });
}
